import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight, Lock, Mail, Users, ChevronDown } from 'lucide-react';
import { useHGS } from '../context/HGSContext';
import { ROLES, DEMO_USERS } from '../data/mockData';
import { ORG_TREE } from '../data/orgTree';
import { cn } from '@/lib/utils';

const ROUTE_FOR_ROLE = {
  department_head: '/my-tasks',
  operating_head: '/dashboard',
  division_head: '/div-dashboard',
  legal: '/grc-review',
  admin: '/admin/grc-dashboard',
};

// Group users for the demo-as dropdown — preserves hierarchy in optgroup labels.
function buildUserGroups() {
  const groups = [];
  // Special roles first
  groups.push({
    label: 'Framework / Read-only',
    users: DEMO_USERS.filter((u) => u.role === 'admin' || u.role === 'legal'),
  });
  // Each Operating Head domain — OH then per-Division (DivH + dept heads)
  Object.values(ORG_TREE).forEach((oh) => {
    const ohUser = DEMO_USERS.find((u) => u.role === 'operating_head' && u.operatingHead === oh.id);
    const ohGroup = { label: `${oh.label} · Operating Head`, users: ohUser ? [ohUser] : [] };
    groups.push(ohGroup);
    Object.values(oh.divisions).forEach((div) => {
      const divUser = DEMO_USERS.find((u) => u.role === 'division_head' && u.division === div.id);
      const deptUsers = DEMO_USERS.filter((u) => u.role === 'department_head' && u.division === div.id);
      groups.push({
        label: `${oh.label} › ${div.label}`,
        users: [divUser, ...deptUsers].filter(Boolean),
      });
    });
  });
  return groups;
}

export default function LoginPage() {
  const { loginAsUser } = useHGS();
  const navigate = useNavigate();
  // Default: pick the first dept-head under the first OH/Division (richest demo flow)
  const initialUser = DEMO_USERS.find((u) => u.role === 'department_head') || DEMO_USERS[0];
  const [userId, setUserId] = useState(initialUser.userId);

  const groups = useMemo(buildUserGroups, []);
  const selectedUser = DEMO_USERS.find((u) => u.userId === userId) || initialUser;
  const role = ROLES.find((r) => r.id === selectedUser.role);

  const [password, setPassword] = useState('demo123');

  const handleSubmit = (e) => {
    e.preventDefault();
    const u = loginAsUser(userId);
    navigate(ROUTE_FOR_ROLE[u.role] || '/dashboard');
  };

  // Quick role-pill filter — clicking a role pre-selects the first user of that role.
  const handleRoleQuickPick = (roleId) => {
    const u = DEMO_USERS.find((x) => x.role === roleId);
    if (u) setUserId(u.userId);
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-900 to-[#1a0000] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 60%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#CC0000] opacity-20 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#CC0000] opacity-10 blur-[120px] rounded-full" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#CC0000] to-[#990000] flex items-center justify-center font-bold text-lg shadow-lg">H</div>
            <div>
              <div className="text-base font-bold tracking-tight">HGS</div>
              <div className="text-[10px] text-slate-400 font-medium tracking-widest uppercase">HMSI Governance System</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur text-[11px] font-medium text-white/80 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#CC0000]" />
              Hierarchical Governance · OH › Division › Department
            </div>
            <h1 className="text-4xl font-bold leading-tight tracking-tight mb-4">
              Each level sees only<br />
              <span className="text-[#FF4444]">what's under their watch.</span>
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Operating Heads own risk and final approval. Divisional Heads review the departments
              under their division. Department Heads execute compliance tasks. HGS keeps every
              action traceable up the hierarchy.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { v: '2', l: 'Operating Heads' },
                { v: '6', l: 'Divisions' },
                { v: '22', l: 'Departments' },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-[#CC0000]/60 pl-3">
                  <div className="text-2xl font-bold">{s.v}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate-500">© 2025 HMSI • Demo prototype • For executive review only</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-slate-50/30">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-1">Pick a demo persona to preview that view.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Demo persona dropdown — primary entry point */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5 uppercase tracking-wider">
                <Users className="w-3 h-3" /> Demo as
              </label>
              <div className="relative">
                <select
                  data-testid="demo-user-select"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="w-full appearance-none px-3.5 py-2.5 pr-10 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-800 focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10 transition"
                >
                  {groups.map((g) => g.users.length > 0 && (
                    <optgroup key={g.label} label={g.label}>
                      {g.users.map((u) => (
                        <option key={u.userId} value={u.userId}>
                          {u.name}
                          {u.role === 'department_head' ? ` — DH, ${u.departmentLabel || u.department}` : ''}
                          {u.role === 'division_head' ? ` — DivH, ${u.divisionLabel || u.division}` : ''}
                          {u.role === 'operating_head' ? ` — OH, ${u.ohLabel || u.operatingHead}` : ''}
                          {u.role === 'admin' ? ' — Admin (Framework Owner)' : ''}
                          {u.role === 'legal' ? ' — Legal / GRC' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              {/* Selected persona detail strip */}
              <div className="mt-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-[11px] text-slate-600 flex items-center gap-2" data-testid="selected-persona">
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#CC0000] to-[#990000] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{selectedUser.avatar}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 truncate">{selectedUser.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {role?.label}
                    {selectedUser.ohLabel && ` · ${selectedUser.ohLabel}`}
                    {selectedUser.divisionLabel && ` › ${selectedUser.divisionLabel}`}
                    {selectedUser.departmentLabel && ` › ${selectedUser.departmentLabel}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Email (auto-filled, editable for show) */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="email-input"
                  type="email"
                  readOnly
                  value={selectedUser.email}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10 transition"
                />
              </div>
            </div>

            {/* Quick role pills — preserved for muscle-memory + tests */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-2 block uppercase tracking-wider">Quick role pick</label>
              <div className="grid grid-cols-2 gap-2" data-testid="role-grid">
                {ROLES.map((r) => {
                  const active = selectedUser.role === r.id;
                  return (
                    <button
                      data-testid={`role-pill-${r.id}`}
                      type="button"
                      key={r.id}
                      onClick={() => handleRoleQuickPick(r.id)}
                      className={cn(
                        'text-left px-3 py-2 rounded-lg border-2 transition-all',
                        active ? 'border-[#CC0000] bg-[#CC0000]/5 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className={cn('w-1.5 h-1.5 rounded-full', active ? 'bg-[#CC0000]' : 'bg-slate-300')} />
                        <span className={cn('text-xs font-bold', active ? 'text-[#CC0000]' : 'text-slate-700')}>{r.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              className="w-full bg-gradient-to-r from-[#CC0000] to-[#990000] hover:from-[#B00000] hover:to-[#770000] text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all group"
            >
              Sign in as {selectedUser.name}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </button>

            <div className="flex items-center gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>Strict hierarchical access — DivH sees their division only, OH sees their operating domain only.</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
